import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import type { OrientedRect } from '../types/oriented-rect'
import { reanchorResize } from './reanchor-resize'

/**
 * Caps a resize result at a maximum width/height (e.g. a photo fill's natural resolution),
 * re-deriving `x`/`y` so the handle's anchor point (opposite corner/edge) stays exactly
 * where it was — a no-op when `next` is already within bounds.
 *
 * Only enforces the ceiling when the gesture *started* within it. If `initial` already
 * exceeds `maxWidth`/`maxHeight` (e.g. a photo placed larger than its own resolution
 * supports, before this resize even began), skip clamping for the whole gesture instead of
 * snapping the shape down to the ceiling on the very first drag tick — that snap would make
 * the shape jump instead of tracking the pointer, which is worse than not enforcing the
 * ceiling at all for an already-oversized shape.
 *
 * `aspectLocked` controls how the two axes are capped when only one of them exceeds its
 * ceiling: for a free resize each axis has its own independent limit, so capping one axis
 * alone is correct (`Math.min` per axis). For an aspect-locked resize (Shift-drag, corner
 * handle, or linked W/H panel fields) both axes must shrink together by the same uniform
 * scale, or the locked ratio gets silently distorted the moment either axis alone crosses
 * its ceiling — the common case whenever the photo's natural aspect differs from the
 * shape's current aspect.
 */
export function clampOrientedResizeToMaxSize(params: {
  initial: OrientedRect
  direction: UnifiedSelectionResizeHandle
  next: { x: number; y: number; width: number; height: number }
  maxWidth?: number | null
  maxHeight?: number | null
  aspectLocked?: boolean
}): { x: number; y: number; width: number; height: number } {
  const { initial, direction, next, maxWidth, maxHeight, aspectLocked } = params
  const w0 = initial.width ?? 0
  const h0 = initial.height ?? 0
  const startedWithinBounds =
    (maxWidth == null || w0 <= maxWidth) && (maxHeight == null || h0 <= maxHeight)

  if (!startedWithinBounds) return next

  let clampedWidth: number
  let clampedHeight: number

  if (aspectLocked) {
    const widthScale = maxWidth != null && next.width > 0 ? maxWidth / next.width : 1
    const heightScale = maxHeight != null && next.height > 0 ? maxHeight / next.height : 1
    const scale = Math.min(1, widthScale, heightScale)

    clampedWidth = next.width * scale
    clampedHeight = next.height * scale
  } else {
    clampedWidth = maxWidth != null ? Math.min(next.width, maxWidth) : next.width
    clampedHeight = maxHeight != null ? Math.min(next.height, maxHeight) : next.height
  }

  if (clampedWidth === next.width && clampedHeight === next.height) return next

  return reanchorResize(initial, direction, clampedWidth, clampedHeight)
}

/**
 * Caps a non-corner ("crop") resize so the frame can't grow past the photo's *current* covered
 * size in either axis — legacy `page-editor` blocks exactly this on the live drag (e.g. the
 * `EAST` handler: `if (rect.width + dx > actualScaleWidth()) dx = actualScaleWidth() - rect.width`).
 *
 * This only matters once the frame's effective size is frozen across the resize (see
 * `computePhotoFillOffsetAfterResize`) instead of the browser's `preserveAspectRatio="slice"`
 * silently re-covering a differently-shaped frame — without this cap, growing an edge past that
 * frozen size would expose empty canvas past the (no-longer-auto-recovering) photo. Each axis is
 * capped independently — this is only ever called for non-corner handles, where the two axes are
 * never locked together.
 */
export function clampCropResizeToCoveredSize(params: {
  initial: OrientedRect
  direction: UnifiedSelectionResizeHandle
  next: { x: number; y: number; width: number; height: number }
  coveredWidth?: number | null
  coveredHeight?: number | null
}): { x: number; y: number; width: number; height: number } {
  const { initial, direction, next, coveredWidth, coveredHeight } = params
  const clampedWidth = coveredWidth != null ? Math.min(next.width, coveredWidth) : next.width
  const clampedHeight = coveredHeight != null ? Math.min(next.height, coveredHeight) : next.height

  if (clampedWidth === next.width && clampedHeight === next.height) return next

  return reanchorResize(initial, direction, clampedWidth, clampedHeight)
}
